import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Calendar, MessageSquare, Euro, Star, Clock, 
  ArrowRight, Users, Check, X, Loader2, Settings, Award, ImageIcon, Shield, HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import OnboardingTutorial, { useOnboarding } from '../../components/OnboardingTutorial';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  pro: "#4A90D9",
  elite: "#9B59B6",
  master: "#F1C40F",
  grandmaster: "#E74C3C",
  legendary: "#E07A5F"
};

const RANK_THRESHOLDS = {
  pro: { missions: 0, rating: 0 },
  elite: { missions: 10, rating: 4.0 },
  master: { missions: 30, rating: 4.3 },
  grandmaster: { missions: 75, rating: 4.6 },
  legendary: { missions: 150, rating: 4.8 }
};

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Onboarding tutorial
  const { showOnboarding, setShowOnboarding, resetOnboarding } = useOnboarding('provider');

  // SAFETY: Redirect admin away from provider dashboard
  useEffect(() => {
    if (user && user.role === 'admin') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await fetch(`${API_URL}/api/dashboard/provider`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDashboardData(data);
        }
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [token]);

  const handleBookingAction = async (bookingId, status) => {
    setActionLoading(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/status?status=${status}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        toast.success(status === 'confirmed' ? t('booking.confirmed') : t('booking.cancelled'));
        const dashRes = await fetch(`${API_URL}/api/dashboard/provider`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
        });
        if (dashRes.ok) {
          const data = await dashRes.json();
          setDashboardData(data);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const getRankName = (rank) => t(`ranks.${rank}`) || rank;
  const getCategoryName = (category) => t(`categories.${category}`) || category;

  const getNextRank = (currentRank) => {
    const ranks = ['pro', 'elite', 'master', 'grandmaster', 'legendary'];
    const idx = ranks.indexOf(currentRank);
    return idx < ranks.length - 1 ? ranks[idx + 1] : null;
  };

  const calculateProgress = (provider) => {
    if (!provider) return 0;
    const nextRank = getNextRank(provider.rank);
    if (!nextRank) return 100;
    
    const currentThreshold = RANK_THRESHOLDS[provider.rank];
    const nextThreshold = RANK_THRESHOLDS[nextRank];
    
    const missionProgress = ((provider.completed_missions - currentThreshold.missions) / 
      (nextThreshold.missions - currentThreshold.missions)) * 50;
    const ratingProgress = ((provider.rating - currentThreshold.rating) / 
      (nextThreshold.rating - currentThreshold.rating)) * 50;
    
    return Math.min(100, Math.max(0, missionProgress + ratingProgress));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const provider = dashboardData?.provider_profile;
  const rankInfo = dashboardData?.rank_info;

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="provider-dashboard">
      <div className="container-app">
        {/* Pending Verification Banner */}
        {provider && provider.verification_status !== 'verified' && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="pending-verification-banner">
            <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">
                {provider.verification_status === 'rejected' ? t('verification.rejectedTitle') : t('verification.pendingTitle')}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                {provider.verification_status === 'rejected' ? t('verification.rejectedMessage') : t('verification.pendingMessage')}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold mb-2">
              {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground">
              {getCategoryName(provider?.service_category)}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/provider/profile')}
            data-testid="edit-profile-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            {t('dashboard.editProfile')}
          </Button>
          <Button
            className="btn-primary"
            onClick={() => navigate('/provider/blog')}
            data-testid="my-posts-btn"
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            {t('blog.myPosts')}
          </Button>
        </div>

        {/* Rank Card */}
        {provider && (
          <Card className="dashboard-card mb-8 overflow-hidden">
            <div 
              className="h-2"
              style={{ backgroundColor: RANK_COLORS[provider.rank] }}
            />
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={provider.profile_image} alt={provider.name} />
                  <AvatarFallback className="text-2xl bg-accent text-accent-foreground">
                    {provider.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="font-heading text-xl font-semibold">{provider.name}</h2>
                    <Badge 
                      style={{ backgroundColor: RANK_COLORS[provider.rank] }}
                      className="text-white border-0"
                    >
                      <Award className="w-3 h-3 mr-1" />
                      {getRankName(provider.rank)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-muted-foreground text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      {provider.rating.toFixed(1)} ({provider.review_count} {t('provider.reviews')})
                    </span>
                    <span>{provider.completed_missions} {t('provider.missions')}</span>
                    <span>{t('provider.commission')}: {(provider.commission_rate * 100).toFixed(0)}%</span>
                  </div>
                  
                  {/* Rank Progress */}
                  {getNextRank(provider.rank) && (
                    <div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span>{t('dashboard.rankProgress')}</span>
                        <span className="text-muted-foreground">
                          {getRankName(getNextRank(provider.rank))}
                        </span>
                      </div>
                      <Progress value={calculateProgress(provider)} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {RANK_THRESHOLDS[getNextRank(provider.rank)].missions - provider.completed_missions} missions & 
                        {(RANK_THRESHOLDS[getNextRank(provider.rank)].rating - provider.rating).toFixed(1)} rating needed
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <Euro className="w-8 h-8 text-accent mx-auto mb-2" />
              <p className="text-2xl font-semibold">{dashboardData?.month_earnings?.toFixed(0) || 0}€</p>
              <p className="text-muted-foreground text-sm">{t('dashboard.monthEarnings')}</p>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <Euro className="w-8 h-8 text-sage mx-auto mb-2" />
              <p className="text-2xl font-semibold">{dashboardData?.total_earnings?.toFixed(0) || 0}€</p>
              <p className="text-muted-foreground text-sm">{t('dashboard.totalEarnings')}</p>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <Calendar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-semibold">{dashboardData?.completed_bookings || 0}</p>
              <p className="text-muted-foreground text-sm">{t('dashboard.completedMissions')}</p>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <MessageSquare className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-semibold">{dashboardData?.unread_messages || 0}</p>
              <p className="text-muted-foreground text-sm">{t('nav.messages')}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pending Bookings */}
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">{t('dashboard.pendingRequests')}</CardTitle>
              <Badge variant="secondary">{dashboardData?.pending_bookings?.length || 0}</Badge>
            </CardHeader>
            <CardContent>
              {dashboardData?.pending_bookings?.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">{t('common.noResults')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData?.pending_bookings?.map((booking) => (
                    <div
                      key={booking.booking_id}
                      className="p-4 rounded-xl bg-muted/50"
                      data-testid={`pending-booking-${booking.booking_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium">{booking.client_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{booking.date} {booking.start_time}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Users className="w-3.5 h-3.5" />
                            <span>{booking.capacity_needed} • {booking.duration_hours}h</span>
                          </div>
                        </div>
                        <p className="font-semibold text-accent">{booking.total_price.toFixed(0)}€</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-success hover:bg-success/90"
                          onClick={() => handleBookingAction(booking.booking_id, 'confirmed')}
                          disabled={actionLoading === booking.booking_id}
                        >
                          {actionLoading === booking.booking_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              {t('booking.accept')}
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleBookingAction(booking.booking_id, 'cancelled')}
                          disabled={actionLoading === booking.booking_id}
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t('booking.decline')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Bookings */}
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">{t('dashboard.upcomingBookings')}</CardTitle>
              <Button
                variant="ghost"
                className="text-accent"
                onClick={() => navigate('/bookings')}
              >
                {t('home.viewAll')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {dashboardData?.upcoming_bookings?.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">{t('common.noResults')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData?.upcoming_bookings?.map((booking) => (
                    <div
                      key={booking.booking_id}
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => navigate(`/bookings/${booking.booking_id}`)}
                    >
                      <div>
                        <p className="font-medium">{booking.client_name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{booking.date}</span>
                          <Clock className="w-3.5 h-3.5 ml-2" />
                          <span>{booking.start_time}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="default">{t('booking.confirmed')}</Badge>
                        <p className="text-sm text-muted-foreground mt-1">{booking.total_price.toFixed(0)}€</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Help button to restart tutorial */}
      <Button
        variant="outline"
        size="icon"
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full shadow-lg bg-background hover:bg-accent hover:text-white z-40"
        onClick={resetOnboarding}
        title="Aide / Tutoriel"
        data-testid="help-tutorial-btn"
      >
        <HelpCircle className="w-6 h-6" />
      </Button>
      
      {/* Onboarding Tutorial */}
      <OnboardingTutorial 
        userRole="provider"
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />
    </div>
  );
}
